/* eslint-disable no-undef */
import React, { Component } from "react";
import { Spin } from "antd";
import moment from "moment";
import mapStyle from "../../config/mapStyle";
import LocateIcon from "../../images/current-location.svg";
import StatusBanner from "./statusBanner";
import {
  BRAND,
  cabIcon,
  CAB_ICON_SIZE,
  stopPinIcon,
  STOP_ICON_SIZE,
  stopLabelColor
} from "./mapIcons";
import less from "./cabTrackView.module.scss";

const TRACKING_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/cabTracking/trackingActive.svg";
const SERVER_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/cabTracking/trackingUnavailable.svg";

const MAX_FIT_ZOOM = 16;
const LOCATE_ZOOM = 15;
const MARKER_GLIDE_MS = 1000;

// Copy from the Tracer requirements: not started, API failure and offline
// each have one message; unavailable (no location yet) and a bad link read
// as an API failure
const STATE_CONTENT = {
  NOT_STARTED: {
    icon: TRACKING_ICON,
    title: "Cab journey not started yet."
  },
  COMPLETED: {
    icon: TRACKING_ICON,
    title: "Tracking unavailable",
    subtitle: "The trip has been completed"
  },
  UNAVAILABLE: {
    icon: SERVER_ICON,
    title: "Unable to retrieve data."
  },
  ERROR: {
    icon: SERVER_ICON,
    title: "Unable to retrieve data."
  },
  OFFLINE: {
    icon: SERVER_ICON,
    title: "Please check your internet connection."
  },
  INVALID: {
    icon: SERVER_ICON,
    title: "Unable to retrieve data."
  }
};

// Shared by the Tracer "Track Cab" popup and the public /cab-track page.
// state: LOADING | LIVE | NOT_STARTED | COMPLETED | UNAVAILABLE | INVALID |
// ERROR | OFFLINE
class CabTrackView extends Component {
  map = null;
  markers = {};
  stopMarkers = [];
  routeLines = [];
  routePath = null;
  infoWindow = null;
  // Keep the cab in view as it moves, until the user pans the map themselves
  following = true;

  componentDidUpdate() {
    this.renderMarkers();
  }

  componentWillUnmount() {
    Object.values(this.markers).forEach(marker => {
      cancelAnimationFrame(marker.glideFrame);
    });
  }

  setMapRef = el => {
    if (!el) {
      this.map = null;
      this.markers = {};
      this.stopMarkers = [];
      this.routeLines = [];
      this.routePath = null;
      this.infoWindow = null;
      return;
    }
    const { locations } = this.props;
    if (typeof google === "undefined" || !locations.length) return;
    this.map = new google.maps.Map(el, {
      zoom: 14,
      center: this.toLatLng(locations[0]),
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: "greedy"
    });
    const styledMapType = new google.maps.StyledMapType(mapStyle);
    this.map.mapTypes.set("styled_map", styledMapType);
    this.map.setMapTypeId("styled_map");
    this.map.addListener("dragstart", () => {
      this.following = false;
    });
    this.infoWindow = new google.maps.InfoWindow();
    this.renderMarkers(true);
  };

  toLatLng = loc => ({ lat: loc.latitude, lng: loc.longitude });

  // Moves the marker to its new position over MARKER_GLIDE_MS instead of
  // jumping between pings
  glideMarker = (marker, to) => {
    const from = marker.getPosition();
    if (!from || (from.lat() === to.lat && from.lng() === to.lng)) return;
    cancelAnimationFrame(marker.glideFrame);
    const start = performance.now();
    const step = now => {
      const t = Math.min(1, (now - start) / MARKER_GLIDE_MS);
      const ease = t * (2 - t);
      marker.setPosition({
        lat: from.lat() + (to.lat - from.lat()) * ease,
        lng: from.lng() + (to.lng - from.lng()) * ease
      });
      if (t < 1) marker.glideFrame = requestAnimationFrame(step);
    };
    marker.glideFrame = requestAnimationFrame(step);
  };

  // next: the stop the cab is heading to or standing at; stops before it
  // are done (the server marks earlier stops reached); later ones upcoming
  getStopState = (index, eta) => {
    if (index < eta.stopIndex || eta.phase === "LEFT") return "done";
    if (index === eta.stopIndex) return "next";
    return "upcoming";
  };

  showStopInfo = (marker, stop) => {
    const time = stop.scheduledTime
      ? ` · ${moment(stop.scheduledTime).format("h:mm A")}`
      : "";
    const wrapper = document.createElement("div");
    wrapper.className = less.infoWindow;
    const title = document.createElement("strong");
    title.textContent = stop.name || "";
    const subtitle = document.createElement("span");
    const { eta } = this.props;
    const pointType =
      eta && eta.leg === "DROP" ? "Drop point" : "Boarding point";
    subtitle.textContent = `${pointType}${time}`;
    wrapper.appendChild(title);
    wrapper.appendChild(subtitle);
    this.infoWindow.setContent(wrapper);
    this.infoWindow.open({ map: this.map, anchor: marker });
  };

  renderStops = (eta, bounds) => {
    const target = eta && eta.target;
    const stops = (eta && eta.stops) || (target ? [target] : []);
    const numbered = stops.length > 1;

    // Every stop gets a pin, but only the current target is fitted - a far
    // later stop shouldn't zoom the map out
    stops.forEach((stop, index) => {
      const state = eta.stops ? this.getStopState(index, eta) : "next";
      const icon = {
        url: stopPinIcon(state, numbered),
        scaledSize: new google.maps.Size(
          STOP_ICON_SIZE.width,
          STOP_ICON_SIZE.height
        ),
        anchor: new google.maps.Point(STOP_ICON_SIZE.width / 2, 43),
        labelOrigin: new google.maps.Point(STOP_ICON_SIZE.width / 2, 16.5)
      };
      const label = numbered
        ? {
            text: String(index + 1),
            color: stopLabelColor(state),
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            fontWeight: "600"
          }
        : null;
      const position = { lat: stop.lat, lng: stop.lng };
      let marker = this.stopMarkers[index];
      if (marker) {
        marker.setPosition(position);
        marker.setIcon(icon);
        marker.setLabel(label);
      } else {
        marker = new google.maps.Marker({
          position,
          map: this.map,
          title: stop.name || "",
          icon,
          label,
          zIndex: 1
        });
        marker.addListener("click", () => this.showStopInfo(marker, stop));
        this.stopMarkers[index] = marker;
      }
    });
    // The stop list can shrink (passenger view, next leg): drop extra pins
    this.stopMarkers
      .slice(stops.length)
      .forEach(marker => marker.setMap(null));
    this.stopMarkers.length = stops.length;
    if (target) {
      bounds.extend({ lat: target.lat, lng: target.lng });
    }
  };

  // Road route from the cab to its next stop (OSRM, encoded polyline): a
  // purple line on a white casing so it reads on the grey map
  renderRoute = eta => {
    const encoded = eta && eta.phase === "EN_ROUTE" ? eta.route : null;
    const canDecode = google.maps.geometry && google.maps.geometry.encoding;
    if (!encoded || !canDecode) {
      this.routeLines.forEach(line => line.setMap(null));
      this.routePath = null;
      return;
    }
    if (encoded === this.routePath) return;
    this.routePath = encoded;

    const path = google.maps.geometry.encoding.decodePath(encoded);
    if (!this.routeLines.length) {
      this.routeLines = [
        { color: "#ffffff", weight: 8, opacity: 1 },
        { color: BRAND, weight: 4, opacity: 0.9 }
      ].map(
        style =>
          new google.maps.Polyline({
            strokeColor: style.color,
            strokeWeight: style.weight,
            strokeOpacity: style.opacity,
            clickable: false,
            zIndex: 0
          })
      );
    }
    this.routeLines.forEach(line => {
      line.setPath(path);
      line.setMap(this.map);
    });
  };

  renderMarkers = (fitBounds = false) => {
    if (!this.map) return;
    const { locations, eta } = this.props;
    const bounds = new google.maps.LatLngBounds();
    const labelCabs = locations.length > 1;

    this.renderRoute(eta);
    this.renderStops(eta, bounds);

    // A cab that stopped reporting (or is no longer live) leaves the map
    const liveIds = locations.map(loc => loc.cabBookingGroupId);
    Object.keys(this.markers).forEach(id => {
      if (!liveIds.includes(id)) {
        this.markers[id].setMap(null);
        delete this.markers[id];
      }
    });

    locations.forEach(loc => {
      const position = this.toLatLng(loc);
      bounds.extend(position);
      const existing = this.markers[loc.cabBookingGroupId];
      if (existing) {
        this.glideMarker(existing, position);
        return;
      }
      this.markers[loc.cabBookingGroupId] = new google.maps.Marker({
        position,
        map: this.map,
        title: loc.vehicleNumber || "",
        zIndex: 2,
        icon: {
          url: cabIcon(),
          scaledSize: new google.maps.Size(CAB_ICON_SIZE, CAB_ICON_SIZE),
          anchor: new google.maps.Point(CAB_ICON_SIZE / 2, CAB_ICON_SIZE / 2),
          labelOrigin: new google.maps.Point(CAB_ICON_SIZE / 2, 62)
        },
        // Several cabs on one map: tag each with its vehicle number
        label:
          labelCabs && loc.vehicleNumber
            ? {
                text: loc.vehicleNumber,
                className: less.cabLabel,
                color: "#5a1dc4",
                fontFamily: "Poppins, sans-serif",
                fontSize: "11px",
                fontWeight: "600"
              }
            : null
      });
    });

    if (fitBounds && (locations.length > 1 || (eta && eta.target))) {
      // Leave room for the status banner along the bottom
      this.map.fitBounds(bounds, { top: 60, right: 60, bottom: 130, left: 40 });
      // A cab standing at the station would otherwise zoom to street level
      google.maps.event.addListenerOnce(this.map, "idle", () => {
        if (this.map && this.map.getZoom() > MAX_FIT_ZOOM) {
          this.map.setZoom(MAX_FIT_ZOOM);
        }
      });
    } else if (!fitBounds && this.following) {
      this.keepCabsInView(bounds);
    }
  };

  keepCabsInView = bounds => {
    const view = this.map.getBounds();
    const { locations } = this.props;
    const outOfView = locations.some(
      loc => view && !view.contains(this.toLatLng(loc))
    );
    if (outOfView) this.map.panToBounds(bounds, 80);
  };

  locateCab = () => {
    const { locations } = this.props;
    if (!this.map || !locations.length) return;
    this.following = true;
    if (locations.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      locations.forEach(loc => bounds.extend(this.toLatLng(loc)));
      this.map.fitBounds(bounds, { top: 60, right: 60, bottom: 130, left: 40 });
      return;
    }
    this.map.panTo(this.toLatLng(locations[0]));
    if (this.map.getZoom() < LOCATE_ZOOM) this.map.setZoom(LOCATE_ZOOM);
  };

  render() {
    const { state, locations = [], fullScreen, eta } = this.props;
    const heightClass = fullScreen ? less.fullScreen : less.fixed;

    if (state === "LOADING") {
      return (
        <div className={`${less.unavailable} ${heightClass}`}>
          <Spin />
        </div>
      );
    }

    if (state === "LIVE" && locations.length) {
      const lastUpdated = Math.max(...locations.map(loc => loc.capturedAt));
      return (
        <div className={`${less.mapWrapper} ${heightClass}`}>
          <div className={less.map} ref={this.setMapRef} />
          <button
            type="button"
            className={less.locateButton}
            onClick={this.locateCab}
            title="Locate cab"
            aria-label="Locate cab"
          >
            <img src={LocateIcon} alt="" />
          </button>
          {eta && locations.length === 1 && (
            <div className={less.statusBanner}>
              <StatusBanner eta={eta} location={locations[0]} />
            </div>
          )}
          <div className={less.lastUpdated}>
            {locations.length === 1 && locations[0].vehicleNumber
              ? `${locations[0].vehicleNumber} · `
              : ""}
            Last updated on {moment(lastUpdated).format("h:mm A")}
          </div>
        </div>
      );
    }

    const content = STATE_CONTENT[state] || STATE_CONTENT.ERROR;
    return (
      <div className={`${less.unavailable} ${heightClass}`}>
        <img src={content.icon} alt="tracking" />
        <p>{content.title}</p>
        {content.subtitle && <span>{content.subtitle}</span>}
      </div>
    );
  }
}

export default CabTrackView;
